import { PplPopoverDirective } from '../../popover';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Host,
  HostBinding,
  HostListener,
  Input,
  Optional,
  Output,
  Renderer2,
  SkipSelf,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { ControlContainer } from '@angular/forms';
import { notFirstChange } from '@ppl/utils/angular.utils';
import { applyFieldColorVariables } from '@ppl/utils/color.utils';
import { listenToValidationChanges } from '@ppl/utils/form-control.utils';
import { MemoizeLast } from '@ppl/utils/memoize.utils';
import { unsubscribe } from '@ppl/utils/subscription.utils';
import type {
  OnChanges,
  OnDestroy,
  OnInit
} from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import type { TSimpleChanges } from '@ppl/utils/angular.utils';
import type { Subscription } from 'rxjs';
import type { PplPopoverAnchorRect } from '../../popover';

@Component({
    selector: 'ppl-input-container',
    templateUrl: './input-container.component.html',
    styleUrls: ['./input-container.component.scss'],
    standalone: false
})
export class PplInputContainerComponent implements OnChanges, OnInit, OnDestroy {
  @Input() label: string | null;
  @Input() description: string | null;
  @Input() bold: boolean;
  @Input() pplFormControl: AbstractControl | null;
  @Input() pplFormControlName: string | null;
  @Input() hasError = false;
  @Input() @HostBinding('class.ppl-input-container--changes') hasChanges = false;
  @Input() @HostBinding('class.italic-notice') italicNotice = false;
  @Input() color: string;
  @Input() notice?: string;
  @Input() disabled = false;
  @Input() showClearSelection = false;
  @Input() required: boolean;
  @Input() errors: any;
  @Input() showErrors = true;
  @Input() showErrorsInTooltip = false;
  @Input() addErrorClass = true;
  @Input() customErrorMessages: { [key: string]: string; };
  @Input() labelContent?: TemplateRef<any>;
  @Input() actionTemplate?: TemplateRef<any>;
  @Input() descriptionHoverPreserve = false;
  @Input() prependIcon: string | null;
  @Input() @HostBinding('attr.field-id') fieldId: string;

  @ContentChild('noticeContent', { static: false }) noticeContent?: TemplateRef<any>;
  @ViewChild('errorsTooltip', { read: PplPopoverDirective, static: false }) errorsTooltipPopover: PplPopoverDirective;

  @Output() undoChanges = new EventEmitter();
  @Output() clearSelection = new EventEmitter();

  private errorsTooltipPopoverAnchorRect: PplPopoverAnchorRect;

  private className = 'ppl-input-container';
  private statusChangesSubscription: Subscription;
  private validationChangesSubscription: Subscription;

  get control() {
    if (this.pplFormControl) {
      return this.pplFormControl;
    }

    if (this.pplFormControlName) {
      const control = this.parent?.control?.get(this.pplFormControlName);
      if (control) {
        return control;
      } else {
        throw new Error(`pplFormControlName must be used with a parent formGroup directive`);
      }
    }

    return null;
  }

  @HostBinding('class.ppl-input-container--error')
  get hasErrorClass() {
    return this.addErrorClass && (
      this.hasError
      || (this.control && this.control.invalid && (this.control.dirty || this.control.touched))
      || this.errors
    );
  }

  get hasRequiredClass() {
    if (this.control && this.control.validator) {
      const validators = this.control.validator({} as AbstractControl);
      return validators && validators.hasOwnProperty('required');
    } else if (this.required === true) {
      return true;
    }
    return false;
  }

  @MemoizeLast<PplInputContainerComponent>(['showErrorsInTooltip', 'showErrors', 'hasErrorClass'])
  get showErrorsTooltip() {
    return this.showErrorsInTooltip && this.showErrors && this.hasErrorClass;
  }

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    @Optional() @Host() @SkipSelf() private parent: ControlContainer
  ) { }

  ngOnChanges(changes: TSimpleChanges<PplInputContainerComponent>): void {
    if (notFirstChange(changes.color)) {
      applyFieldColorVariables(this.elementRef.nativeElement, this.renderer, this.color);
    }

    if (
      notFirstChange(changes.pplFormControl)
      || notFirstChange(changes.pplFormControlName)
    ) {
      this.applyFormControlSubscriptions();
    }
  }

  ngOnInit() {
    this.renderer.addClass(this.elementRef.nativeElement, this.className);
    applyFieldColorVariables(this.elementRef.nativeElement, this.renderer, this.color);

    this.applyFormControlSubscriptions();
  }

  ngOnDestroy(): void {
    unsubscribe(this.statusChangesSubscription);
    unsubscribe(this.validationChangesSubscription);
  }

  @HostListener('mousemove', ['$event'])
  onMoveErrorsTooltip(event: MouseEvent) {
    if (this.showErrorsTooltip && this.errorsTooltipPopover) {
      this.updateErrorsTooltipPosition(this.errorsTooltipPopover, event);
      this.errorsTooltipPopover.show();
    }
  }

  @HostListener('mouseleave')
  onHideErrorsTooltip() {
    if (this.showErrorsTooltip && this.errorsTooltipPopover) {
      this.errorsTooltipPopover.hide();
    }
  }

  private updateErrorsTooltipPosition(
    errorsTooltipPopover: PplPopoverDirective,
    event: MouseEvent
  ) {
    this.errorsTooltipPopoverAnchorRect = {
      left: event.pageX + ErrorsTooltipMouseOffsetOffsetPx,
      right: event.pageX + ErrorsTooltipMouseOffsetOffsetPx,
      top: event.pageY + ErrorsTooltipMouseOffsetOffsetPx,
      bottom: event.pageY + ErrorsTooltipMouseOffsetOffsetPx,
      height: 0,
      width: 0
    };
    errorsTooltipPopover.recalcPosition();
  }

  private applyFormControlSubscriptions() {
    unsubscribe(this.statusChangesSubscription);
    unsubscribe(this.validationChangesSubscription);

    const {
      statusChangesSubscription,
      validationChangesSubscription
    } = listenToValidationChanges(this.control, this.changeDetectorRef);

    this.statusChangesSubscription = statusChangesSubscription;
    this.validationChangesSubscription = validationChangesSubscription;
  }
}

const ErrorsTooltipMouseOffsetOffsetPx = 5;
